import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import toast from "react-hot-toast";

// Icons
import {
  Camera,
  Loader2,
  LogOut,
  Mail,
  ShieldCheck,
  User as UserIcon,
} from "lucide-react";

// Components
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

// Libs & Hooks
import { useUser } from "@/hooks/useUser";
import api from "@/lib/axios";
import { getInitials } from "@/lib/utils";

const sendResetPasswordLink = async (email: string) => {
  const response = await api.post("/auth/send-reset-password-link", {
    email,
    lang: localStorage.getItem("lang") ?? "id",
  });
  return response.data;
};

export default function ProfilePage() {
  const { user } = useUser();

  const { mutate, isPending } = useMutation({
    mutationFn: sendResetPasswordLink,
    onSuccess: (data) => {
      toast.success(data?.message || "Tautan reset password telah dikirim.");
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>;
      toast.error(
        err?.response?.data?.message || "Terjadi kesalahan yang tidak terduga.",
      );
    },
  });

  const handleSendResetPasswordLink = () => {
    if (!user) return;
    mutate(user?.email);
  };

  return (
    <main className="min-h-screen p-4 sm:p-8 transition-colors duration-300">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* --- HEADER / IDENTITY CARD --- */}
        <div className="relative w-full rounded-2xl bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
          {/* Banner Background */}
          <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-900 dark:to-indigo-900 relative">
            <div className="absolute inset-0 bg-white/10 dark:bg-black/20" />
          </div>

          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row items-center sm:items-end -mt-12 gap-4">
              {/* Avatar with Ring */}
              <div className="relative group">
                <Avatar className="h-24 w-24 border-4 border-white dark:border-slate-900 shadow-md bg-white dark:bg-slate-800">
                  <AvatarImage
                    src={""}
                    alt={user?.nama_lengkap}
                    className="object-cover"
                  />
                  <AvatarFallback className="font-bold text-2xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    {getInitials(user?.nama_lengkap || "U")}
                  </AvatarFallback>
                </Avatar>
                {/* Tombol edit foto */}
                <div className="absolute bottom-0 right-0 bg-white dark:bg-slate-800 rounded-full p-1.5 shadow-sm border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 cursor-pointer hover:text-primary dark:hover:text-primary transition-colors">
                  <Camera className="w-4 h-4" />
                </div>
              </div>

              {/* Name & Role Info */}
              <div className="flex-1 text-center sm:text-left space-y-1 mt-2 sm:mt-14">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {user?.nama_lengkap}
                </h1>
                <div className="flex items-center justify-center sm:justify-start gap-2 text-slate-500 dark:text-slate-400 text-sm font-medium">
                  <Badge
                    variant="secondary"
                    className="rounded-full px-2 py-0.5 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 border-none"
                  >
                    {user?.jenis_user?.[0] || "User"}
                  </Badge>
                  <span className="text-slate-300 dark:text-slate-600">•</span>
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    {user?.email}
                  </span>
                </div>
              </div>

              {/* Optional: Edit Profile Button */}
              {/* <div className="hidden sm:block mb-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  Edit Profil
                </Button>
              </div> */}
            </div>
          </div>
        </div>

        {/* --- CONTENT GRID --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left Col: Personal Details */}
          <Card className="md:col-span-2 shadow-sm border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-white">
                <UserIcon className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                Informasi Pribadi
              </CardTitle>
              <CardDescription className="text-slate-500 dark:text-slate-400">
                Detail akun yang terdaftar di sistem.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Nama Lengkap
                  </label>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 p-2.5 rounded-full bg-slate-50 dark:bg-slate-950/50">
                    {user?.nama_lengkap}
                  </p>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Email
                  </label>
                  <p className="text-sm font-medium text-slate-900 dark:text-slate-100 border border-slate-200 dark:border-slate-700 p-2.5 rounded-full bg-slate-50 dark:bg-slate-950/50">
                    {user?.email}
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Status Akun
                  </label>
                  <div className="flex items-center gap-2 border border-slate-200 dark:border-slate-700 p-2.5 rounded-full bg-slate-50 dark:bg-slate-950/50">
                    <div className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      Aktif
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Col: Security & Actions */}
          <div className="space-y-6">
            <Card className="shadow-sm border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-900 dark:text-white">
                  <ShieldCheck className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                  Keamanan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-slate-900 dark:text-slate-200 mb-1">
                    Password
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
                    Lupa password Anda? Kami akan mengirimkan tautan reset ke
                    email terdaftar.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-center border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-primary dark:text-slate-300"
                    onClick={handleSendResetPasswordLink}
                    disabled={isPending}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        Mengirim...
                      </>
                    ) : (
                      "Kirim Link Reset Password"
                    )}
                  </Button>
                </div>

                <Separator className="bg-slate-200 dark:bg-slate-800" />

                <div>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 h-9 px-2"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Keluar Aplikasi
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </main>
  );
}
